/**
 * The Note cell's facts for one planned drop: group tag (multi-group only),
 * clamps, scaling, the >24h flag, the Hour Optimizer mark (an extra drop in
 * a ranked hour, or a probe into an hour with too little data) and the
 * runtime state words — and, Phase 9, sent / auto-sent or why auto-send
 * holds it. Controls stay in the row.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { HelpCircle } from 'lucide-react';

import type { DayPlanDropRow } from '../../drop-generator.types';
import { ExperimentMark } from '../shared/experiment/ExperimentMark';

interface Props {
  drop: DayPlanDropRow;
}

const hourLabel = (h: number) => `${String(h).padStart(2, '0')}:00–${String((h + 1) % 24).padStart(2, '0')}:00`;

export const TimelineNote: FC<Props> = ({ drop }) => (
  <>
    {drop.optimizer ? (
      <span
        className="text-primary"
        title={
          drop.optimizer.kind === 'extra'
            ? `The Hour Optimizer added this drop: ${hourLabel(drop.optimizer.hour)} ranks #${drop.optimizer.rank} of 24 on this campaign's hour formula — the best hour inside this container's span with room for one more. Container default amount, no slot row to skip — turn the optimizer off to remove it.`
            : `A probe: ${hourLabel(drop.optimizer.hour)} has too little traffic to rank, so one drop goes there to learn. Turn the optimizer off to remove it.`
        }
      >
        {drop.optimizer.kind === 'extra'
          ? `⚡ extra — ${hourLabel(drop.optimizer.hour)} ranks #${drop.optimizer.rank} of 24`
          : `⚡ probe — ${hourLabel(drop.optimizer.hour)} not ranked yet`}
      </span>
    ) : null}
    {drop.hitAgain ? (
      <span className="text-success" title="Sent again right after the drop that hit the Hit again rule (Verdicts › Hit again): same list, same gateway, its texts. No slot row — nothing to skip or disable.">
        ↻ hit again #{drop.hitAgain.no} — {drop.hitAgain.note}
      </span>
    ) : null}
    {drop.hourNote ? <span className="text-success">{drop.hourNote}</span> : null}
    {drop.experiment ? <ExperimentMark /> : null}
    {drop.outsideWindow ? (
      <span className="text-warning" title="Outside the country's window — sent because its container, or this drop, goes outside the window (Schedule › Outside the window, or the drop's row).">
        outside the window — sent
      </span>
    ) : null}
    {drop.listFirstDay ? (
      <span className="text-primary" title="A new list (tagged New) this container's group had never sent to before today — the text policy may give it proven texts (Creative › Text ranking › Config).">
        first day on this list
      </span>
    ) : null}
    {drop.clamped ? <span>clamped — {drop.listName ?? 'list'} capacity</span> : null}
    {drop.capacityUnknown ? (
      <span className="inline-flex items-center text-muted-foreground/70" title="Lead count not fetched — this amount is not capacity-checked against the list" aria-label="capacity unknown">
        <HelpCircle className="h-3 w-3" aria-hidden />
      </span>
    ) : null}
    {drop.scaled && drop.scaled.steps > 0 ? (
      <span className="text-success">
        scaled ×{drop.scaled.factor} from {drop.scaled.from.toLocaleString('en-US')} ({drop.scaled.steps} step
        {drop.scaled.steps === 1 ? '' : 's'})
      </span>
    ) : drop.scaled ? (
      <span className="text-warning">
        shrunk ×{drop.scaled.factor} from {drop.scaled.from.toLocaleString('en-US')} ({-drop.scaled.steps} step
        {drop.scaled.steps === -1 ? '' : 's'} down)
      </span>
    ) : null}
    {drop.returnTest ? <span className="text-warning">test day — the list is back from a burn (usual {drop.returnTest.from.toLocaleString('en-US')})</span> : null}
    {drop.weekend ? (
      <span className="text-muted-foreground">
        weekend — {drop.weekend.pct} % of {drop.weekend.from.toLocaleString('en-US')}
      </span>
    ) : null}
    {drop.exceeds24h ? <span>&gt;24h — sent later by hand</span> : null}
    {drop.runtime?.tombstoned && drop.runtime.skipReason?.startsWith('stopped') ? (
      <span className="text-warning">{drop.runtime.skipReason}</span>
    ) : drop.runtime?.tombstoned ? (
      <span className="text-warning">skipped today — won&apos;t recreate</span>
    ) : drop.runtime?.pinned ? (
      <span>pinned — your edits win (today only)</span>
    ) : drop.runtime && !drop.runtime.hasDraft ? (
      <span>draft deleted — Generate recreates it</span>
    ) : null}
    {drop.runtime?.sentAt ? (
      <span className="text-success">{drop.runtime.sentBy === 'ENGINE' ? 'auto-sent' : 'sent'}</span>
    ) : drop.runtime?.sending ? (
      <span className="text-warning">sending — outcome not known yet</span>
    ) : drop.runtime?.autoSendNote ? (
      <span className="text-warning">{drop.runtime.autoSendNote}</span>
    ) : null}
  </>
);
